import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  acceptMahjong,
  askMahjongHint,
  declineMahjong,
  getMahjong,
  leaveMahjong,
  nudgeMahjong,
  playMahjong,
  sayAtMahjong,
  type GameAgentPick,
  type MahjongMeld,
  type MahjongPlay,
  type MahjongSeat,
  type MahjongView,
} from '../chatv1/transport'
import { onChatv1Frame, onChatv1Open } from '../chatv1/ws'
import { Icon } from '../ui/Icon'
import { Sheet } from '../ui/Sheet'
import { Avatar } from '../chat/Avatar'
import { AgentPick } from './AgentPick'
import { TalkBox } from './GameTalk'
import { MahjongTile } from './MahjongTile'
import { WIND_HAN, kindHan, kindName, kindOf } from './mahjongTiles'
import { gameErrorText } from './gameText'
import './games.css'
import './mahjong.css'

/**
 * A MAHJONG TABLE, FULL SCREEN — owner, 2026-10-26: 開始做麻將; 邀請者可以預設籌碼.
 *
 * You at the bottom with your hand; the player after you on your right, across from you at the top, the one before
 * you (whose discards you may chow) on your left — each with their chips, their wind, the sets they laid down and
 * their flowers, and between you all the four rivers of discards around the round's wind and the tiles left.
 *
 * Your turn: tap a tile to lift it, tap it again (or Discard) to throw it; Win and Kong appear when you may. Someone
 * else's discard you may take: Win, Kong, Pong, Chow or Pass, while the bar runs down. Every decision has its time —
 * when it is up, the computer plays it (the service decides; this screen only asks it, once, when the bar is empty).
 * The table is the service's: it comes by `mahjong` frames, is read again when the socket returns, and a discard
 * shows at once and is then confirmed or put back.
 */

function newer(a: MahjongView | undefined, b: MahjongView): MahjongView {
  return a && a.v > b.v ? a : b
}

const POS = ['bottom', 'right', 'top', 'left'] as const
type Pos = (typeof POS)[number]

const seatName = (t: MahjongView, s: number): string => {
  const p = t.seats[s]
  if (!p) return 'Someone'
  if (p.you) return 'You'
  return p.name || (p.kind === 'house' ? 'Computer' : 'Someone')
}
/** "your", or "Computer 2’s". */
const whose = (t: MahjongView, s: number): string => (t.seats[s]?.you ? 'your' : `${seatName(t, s)}’s`)

/** The table's chips, as a person writes them: 1,250. */
const chips = (n: number): string => n.toLocaleString('en-US')
const signed = (n: number): string => (n > 0 ? `+${chips(n)}` : n < 0 ? `−${chips(-n)}` : '0')

export function MahjongScreen({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient()
  const key = useMemo(() => ['mahjong', id], [id])
  const q = useQuery({ queryKey: key, queryFn: () => getMahjong(id) })
  useEffect(() => {
    const offFrame = onChatv1Frame((f) => {
      if (f.type === 'mahjong' && f.game.id === id) qc.setQueryData<MahjongView>(key, (old) => newer(old, f.game))
    })
    const offOpen = onChatv1Open(() => void qc.invalidateQueries({ queryKey: key }))
    return () => {
      offFrame()
      offOpen()
    }
  }, [id, key, qc])

  const t = q.data
  const [selected, setSelected] = useState<number | null>(null)
  const [thrown, setThrown] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [assistOpen, setAssistOpen] = useState(false)
  const [advisor, setAdvisor] = useState<GameAgentPick | null>(null)
  const [armedLeave, setArmedLeave] = useState(false)
  const [retries, setRetries] = useState(0)

  // A new decision clears a half-made choice.
  const seq = t?.phase?.seq ?? -1
  const [seenSeq, setSeenSeq] = useState(seq)
  if (seenSeq !== seq) {
    setSeenSeq(seq)
    setSelected(null)
    setThrown(null)
    setRetries(0)
  }

  const send = async (fn: () => Promise<{ mahjong: MahjongView }>): Promise<MahjongView | null> => {
    setErr(null)
    setBusy(true)
    try {
      const r = await fn()
      qc.setQueryData<MahjongView>(key, (old) => newer(old, r.mahjong))
      return r.mahjong
    } catch (e) {
      setErr(gameErrorText(e))
      setThrown(null)
      void q.refetch()
      return null
    } finally {
      setBusy(false)
    }
  }
  const play = (p: MahjongPlay) => send(() => playMahjong(id, p))

  // THE CLOCK — one wait to the moment the decision's time is up, then one question to the service (which plays the
  // seat that ran out, or answers that it is not time yet, by its own clock); asked again a little later only when
  // the two clocks disagreed. No clock runs while nothing is awaited.
  const deadline = t?.status === 'active' ? (t.phase?.deadline ?? null) : null
  const vNow = t?.v ?? 0
  const vRef = useRef(vNow)
  vRef.current = vNow
  useEffect(() => {
    if (deadline === null || retries > 8) return
    const due = Math.max(retries ? 3000 : 500, deadline * 1000 + 1000 - Date.now())
    const timer = setTimeout(() => {
      const before = vRef.current
      void playMahjong(id, { action: 'tick' })
        .then((r) => {
          qc.setQueryData<MahjongView>(key, (old) => newer(old, r.mahjong))
          if (r.mahjong.v === before) setRetries((n) => n + 1)
        })
        .catch(() => setRetries((n) => n + 1))
    }, due)
    return () => clearTimeout(timer)
  }, [deadline, seq, retries, id, key, qc])

  if (!t) {
    return createPortal(
      <div className="gm-screen mj-screen" role="dialog" aria-label="Mahjong">
        <Top onClose={onClose} />
        {q.isPending && <p className="gh-note gm-note">Loading the table…</p>}
        {q.isError && <p className="gh-note gm-note mch-err">{gameErrorText(q.error)}</p>}
      </div>,
      document.body,
    )
  }

  const me = t.you ?? 0
  const at = (pos: Pos): number => (me + POS.indexOf(pos)) % 4
  const mine = t.seats[me]!
  const p = t.phase
  const myTurn = !!p && p.t === 'turn' && p.yours && t.status === 'active'
  const hintTile = t.hint?.act === 'discard' ? t.hint.tile : null
  const discard = (tile: number) => {
    if (!p || !myTurn || busy) return
    setThrown(tile)
    setSelected(null)
    void play({ action: 'discard', tile, seq: p.seq })
  }
  const askHint = async (a: GameAgentPick) => {
    setAdvisor(a)
    setAssistOpen(false)
    await send(() => askMahjongHint(id, a))
  }
  const people = t.seats.filter((s, i) => s.kind === 'user' && i !== t.you && !s.left)

  return createPortal(
    <div className="gm-screen mj-screen" role="dialog" aria-label="Mahjong">
      <Top onClose={onClose} t={t} />
      <div className="mj-body">
        <div className="mj-play">
          <div className="mj-table" aria-label="The table">
            {(['top', 'left', 'right'] as const).map((pos) => (
              <SeatPanel key={pos} t={t} s={at(pos)} pos={pos} />
            ))}
            {(['top', 'left', 'right', 'bottom'] as const).map((pos) => (
              <River key={pos} t={t} s={at(pos)} pos={pos} thrown={pos === 'bottom' ? thrown : null} />
            ))}
            <Compass t={t} me={me} />
          </div>
          <Mine
            t={t}
            seat={mine}
            selected={selected}
            thrown={thrown}
            hintTile={hintTile}
            live={myTurn && !busy && !thrown}
            onTap={(tile) => (selected === tile ? discard(tile) : setSelected(tile))}
          />
          <Bar
            t={t}
            busy={busy}
            selected={selected}
            onDiscard={() => selected !== null && discard(selected)}
            onPlay={(x) => void play(x)}
            onNext={() => void play({ action: 'next' })}
            onAccept={() => void send(() => acceptMahjong(id))}
            onDecline={() => void send(() => declineMahjong(id))}
            onStart={() => void play({ action: 'start' })}
            onNudge={() => void send(() => nudgeMahjong(id))}
            onClose={onClose}
          />
          {err && <p className="gh-note mch-err">{err}</p>}
        </div>
        <div className="mj-side">
          {(t.hint || t.hint_pending) && p?.yours && (
            <div className="gm-hint">
              <Icon name="auto_awesome" size={16} />
              {t.hint ? (
                <span>
                  <strong>{hintWords(t.hint)}</strong>
                  {t.hint.note ? ` — ${t.hint.note}` : ''}
                  <span className="gm-hint-who"> · {t.advisor?.name ?? 'your agent'}</span>
                </span>
              ) : (
                <span>Asking {t.advisor?.name ?? 'your agent'} for a hint…</span>
              )}
            </div>
          )}
          {t.status === 'active' && t.you !== null && !mine.left && (
            <div className="gm-actions">
              <button
                type="button"
                className="fr-btn gm-hint-btn"
                disabled={!p?.yours || p.t === 'done' || busy || t.hint_pending}
                onClick={() => (advisor && mine.assist ? void askHint(advisor) : setAssistOpen(true))}
              >
                <Icon name="auto_awesome" size={15} />
                Hint
              </button>
              {/* LEAVING (owner, 2026-10-19: 當使用者退出棋局的時候對方也要收到通知): the computer takes your seat. */}
              {armedLeave ? (
                <span className="fr-btns">
                  <button
                    type="button"
                    className="fr-btn danger"
                    disabled={busy}
                    onClick={() => {
                      setArmedLeave(false)
                      void send(() => leaveMahjong(id))
                    }}
                  >
                    Leave the table?
                  </button>
                  <button type="button" className="fr-btn" onClick={() => setArmedLeave(false)}>
                    Keep playing
                  </button>
                </span>
              ) : (
                <button type="button" className="fr-btn" disabled={busy} onClick={() => setArmedLeave(true)}>
                  <Icon name="logout" size={15} />
                  Leave table
                </button>
              )}
            </div>
          )}
          <Scores t={t} />
          {t.you !== null && !mine.left && people.length > 0 && (
            <TalkBox
              to={people.length === 1 ? seatName(t, t.seats.indexOf(people[0]!)) : 'the table'}
              lines={t.chat.map((c, i) => ({ key: `${c.at}-${i}`, mine: c.from === t.you, who: seatName(t, c.from), text: c.text }))}
              send={async (text) => {
                const r = await sayAtMahjong(id, text)
                qc.setQueryData<MahjongView>(key, (old) => newer(old, r.mahjong))
              }}
            />
          )}
        </div>
      </div>
      {assistOpen && (
        <AssistSheet on={mine.assist} picked={advisor} onCancel={() => setAssistOpen(false)} onAsk={(a) => void askHint(a)} />
      )}
    </div>,
    document.body,
  )
}

function Top({ onClose, t }: { onClose: () => void; t?: MahjongView }) {
  return (
    <div className="gm-top">
      <button type="button" className="gm-back" onClick={onClose} aria-label="Close the game">
        <Icon name="chevron_left" size={22} />
        <span>Games</span>
      </button>
      <span className="gm-title">Mahjong</span>
      {t && t.status !== 'invited' && t.round.hand > 0 && (
        <span className="mj-round" aria-label={`${roundName(t)}, deal ${t.round.hand}`}>
          <span className="mj-round-wind">{WIND_HAN[t.round.wind]}</span>
          <span>Deal {t.round.hand}</span>
        </span>
      )}
    </div>
  )
}

const WIND_WORD: Record<string, string> = { E: 'East', S: 'South', W: 'West', N: 'North' }
const roundName = (t: MahjongView) => `${WIND_WORD[t.round.wind]} round`

function Face({ seat }: { seat: MahjongSeat }) {
  if (seat.kind === 'user') return <Avatar picture={seat.picture} label={seat.name || 'Someone'} className="gm-face mj-face" />
  return (
    <span className="gm-agent-face mj-face" aria-hidden="true">
      <Icon name={seat.kind === 'agent' ? 'smart_toy' : 'computer'} size={16} />
    </span>
  )
}

/** The badges every seat wears: its wind, the dealer's 莊, AI assist, an agent at work. */
function Badges({ t, s }: { t: MahjongView; s: number }) {
  const seat = t.seats[s]!
  return (
    <>
      {seat.wind && (
        <span className={`mj-wind${seat.dealer ? ' dealer' : ''}`} title={`${WIND_WORD[seat.wind]} seat${seat.dealer ? ', dealer' : ''}`}>
          {WIND_HAN[seat.wind]}
        </span>
      )}
      {seat.dealer && t.round.streak > 0 && <span className="mj-streak">連{t.round.streak}</span>}
      {t.result?.win?.seat === s && <span className="mj-hu">胡</span>}
      {seat.assist && (
        <span className="gm-chip gm-assist" title="Uses AI assist in this game">
          <Icon name="auto_awesome" size={11} />
          AI
        </span>
      )}
    </>
  )
}

function Melds({ melds, small }: { melds: MahjongMeld[]; small?: boolean }) {
  if (!melds.length) return null
  return (
    <span className="mj-melds">
      {melds.map((m, i) => (
        <span key={i} className={`mj-meld${small ? ' small' : ''}`}>
          {m.type === 'hidden_kong' && !m.tiles.length
            ? [0, 1, 2, 3].map((j) => <MahjongTile key={j} tile={null} />)
            : m.tiles.map((x, j) => (
                <MahjongTile key={x} tile={m.type === 'hidden_kong' && (j === 0 || j === 3) ? null : x} title={kindName(kindOf(x))} />
              ))}
        </span>
      ))}
    </span>
  )
}

function Flowers({ tiles }: { tiles: number[] }) {
  if (!tiles.length) return null
  return (
    <span className="mj-flowers" aria-label={`Flowers: ${tiles.map((x) => kindName(kindOf(x))).join(', ')}`}>
      {tiles.map((x) => (
        <MahjongTile key={x} tile={x} />
      ))}
    </span>
  )
}

/** Another player: who, their chips and wind, how many tiles they hold (or, after the deal, which), sets and flowers. */
function SeatPanel({ t, s, pos }: { t: MahjongView; s: number; pos: Pos }) {
  const seat = t.seats[s]!
  const turn = t.status === 'active' && t.phase?.t === 'turn' && t.phase.seat === s
  const won = t.result?.win?.seat === s
  return (
    <div className={`mj-seat mj-seat-${pos}${turn ? ' to-move' : ''}${won ? ' won' : ''}${seat.left ? ' gone' : ''}`}>
      <div className="mj-seat-who">
        <Face seat={seat} />
        <span className="mj-seat-name">
          <span className="mj-name">{seat.name}</span>
          <span className="mj-chips">{chips(seat.chips)}</span>
        </span>
        <Badges t={t} s={s} />
      </div>
      {seat.thinking && <span className="mj-thinking">thinking…</span>}
      {seat.left && <span className="mj-thinking">left — the computer plays</span>}
      {seat.invite === 'pending' && <span className="mj-thinking">invited</span>}
      <div className="mj-seat-tiles">
        {seat.tiles ? (
          <span className="mj-open">
            {seat.tiles.map((x) => (
              <MahjongTile key={x} tile={x} title={kindName(kindOf(x))} />
            ))}
          </span>
        ) : (
          seat.held > 0 && (
            <span className="mj-backs" aria-label={`${seat.held} tiles`}>
              {Array.from({ length: Math.min(seat.held, pos === 'top' ? 17 : 4) }, (_, i) => (
                <MahjongTile key={i} tile={null} />
              ))}
              {pos !== 'top' && <span className="mj-held">{seat.held}</span>}
            </span>
          )
        )}
        <Melds melds={seat.melds} small />
        <Flowers tiles={seat.flowers} />
      </div>
    </div>
  )
}

/** A seat's discards, oldest first; the newest discard on the table is marked. */
function River({ t, s, pos, thrown }: { t: MahjongView; s: number; pos: Pos; thrown: number | null }) {
  const tiles = thrown !== null ? [...t.seats[s]!.river, thrown] : t.seats[s]!.river
  const last = t.log.filter((e) => e.act === 'discard').at(-1)
  const onOffer = t.phase?.t === 'claim' ? t.phase.tile : null
  return (
    <div className={`mj-river mj-river-${pos}`} aria-label={`${seatName(t, s)}’s discards`}>
      {tiles.map((x) => (
        <MahjongTile
          key={x}
          tile={x}
          title={kindName(kindOf(x))}
          className={x === onOffer || x === thrown || (x === last?.tile && last.seat === s) ? 'mj-newest' : undefined}
        />
      ))}
    </div>
  )
}

/** The middle of the table: the round's wind, the deal, and the tiles left to draw. */
function Compass({ t, me }: { t: MahjongView; me: number }) {
  const turn = t.phase?.t === 'turn' ? t.phase.seat : t.phase?.t === 'claim' ? t.phase.seat : null
  const rel = turn === null ? null : POS[(turn - me + 4) % 4]
  return (
    <div className="mj-compass">
      <span className="mj-compass-wind" title={roundName(t)}>
        {WIND_HAN[t.round.wind]}
      </span>
      <span className="mj-compass-wall">{t.round.wall} left</span>
      {rel && <span className={`mj-compass-turn to-${rel}`} aria-hidden="true" />}
    </div>
  )
}

/** Your own corner: what you laid down, your flowers, and your hand — the drawn tile set a little apart. */
function Mine({
  t,
  seat,
  selected,
  thrown,
  hintTile,
  live,
  onTap,
}: {
  t: MahjongView
  seat: MahjongSeat
  selected: number | null
  thrown: number | null
  hintTile: number | null
  live: boolean
  onTap: (tile: number) => void
}) {
  if (t.you === null || !seat.tiles) {
    return t.status === 'invited' ? null : <SeatPanel t={t} s={t.you ?? 0} pos="bottom" />
  }
  const drawn = t.hand?.drawn ?? null
  const tiles = seat.tiles.filter((x) => x !== thrown)
  const held = drawn !== null && tiles.includes(drawn) ? [...tiles.filter((x) => x !== drawn), drawn] : tiles
  const hintKind = hintTile === null ? null : kindOf(hintTile)
  return (
    <div className={`mj-mine${t.phase?.t === 'turn' && t.phase.yours ? ' to-move' : ''}${t.result?.win?.seat === t.you ? ' won' : ''}`}>
      <div className="mj-mine-who">
        <Face seat={seat} />
        <span className="mj-name">{seat.name} (you)</span>
        <span className="mj-chips">{chips(seat.chips)}</span>
        <Badges t={t} s={t.you} />
        <Melds melds={seat.melds} />
        <Flowers tiles={seat.flowers} />
      </div>
      <div className={`mj-hand${live ? ' live' : ''}`} style={{ ['--n' as string]: String(held.length) }}>
        {held.map((x) => (
          <button
            key={x}
            type="button"
            className={`mj-htile${x === selected ? ' sel' : ''}${x === drawn ? ' drawn' : ''}${hintKind !== null && kindOf(x) === hintKind ? ' hinted' : ''}`}
            aria-label={`${kindName(kindOf(x))}${x === drawn ? ', just drawn' : ''}${x === selected ? ', lifted — tap again to discard' : ''}`}
            aria-pressed={x === selected ? true : undefined}
            disabled={!live}
            onClick={() => onTap(x)}
            onDoubleClick={() => live && x === selected && onTap(x)}
          >
            <MahjongTile tile={x} />
          </button>
        ))}
      </div>
    </div>
  )
}

/** The time left for the decision awaited — a bar that runs down by itself (CSS), restarted for each decision. */
function TimeBar({ t }: { t: MahjongView }) {
  const p = t.phase
  if (!p || p.deadline === null || t.status !== 'active') return null
  return (
    <div className={`mj-timebar${p.yours ? ' yours' : ''}`} aria-hidden="true">
      <Run key={`${p.seq}-${p.deadline}`} deadline={p.deadline} />
    </div>
  )
}

/** One run of the bar: its length fixed when the decision began here, so a re-render never moves it. */
function Run({ deadline }: { deadline: number }) {
  const [left] = useState(() => Math.max(0, deadline - Date.now() / 1000))
  return <span style={{ animationDuration: `${left}s` }} />
}

function hintWords(h: NonNullable<MahjongView['hint']>): string {
  if (h.act === 'discard' && h.tile !== null) return `Discard ${kindHan(kindOf(h.tile))}`
  if (h.act === 'kong' && h.tile !== null) return `Kong ${kindHan(kindOf(h.tile))}`
  if (h.act === 'chow' && h.with) return `Chow with ${h.with.map((k) => kindHan(k)).join(' ')}`
  return { win: 'Win', pung: 'Pong', pass: 'Pass', kong: 'Kong' }[h.act] ?? h.act
}

/** What the table waits on, and what you may do — under your hand. */
function Bar({
  t,
  busy,
  selected,
  onDiscard,
  onPlay,
  onNext,
  onAccept,
  onDecline,
  onStart,
  onNudge,
  onClose,
}: {
  t: MahjongView
  busy: boolean
  selected: number | null
  onDiscard: () => void
  onPlay: (p: MahjongPlay) => void
  onNext: () => void
  onAccept: () => void
  onDecline: () => void
  onStart: () => void
  onNudge: () => void
  onClose: () => void
}) {
  const p = t.phase
  const o = t.options
  const me = t.you
  if (t.status === 'invited') {
    const pending = t.seats.filter((s) => s.invite === 'pending')
    const mineOpen = me !== null && t.seats[me]?.invite === 'pending'
    return (
      <div className="mj-bar">
        <p className="mj-say" role="status">
          {mineOpen
            ? `${t.seats.find((s) => s.invite === null && s.kind === 'user' && !s.you)?.name ?? 'A friend'} invites you to mahjong — ${chips(t.settings.chips)} chips, ${t.settings.base}/${t.settings.per_tai}${t.settings.rounds === 4 ? ', four rounds' : ''}.`
            : `Waiting for ${pending.map((s) => s.name).join(' and ')} to answer.`}
        </p>
        <div className="mj-acts">
          {mineOpen && (
            <>
              <button type="button" className="sheet-btn primary" disabled={busy} onClick={onAccept}>
                Accept
              </button>
              <button type="button" className="fr-btn" disabled={busy} onClick={onDecline}>
                Decline
              </button>
            </>
          )}
          {t.invited === 'you' && (
            <>
              <button type="button" className="sheet-btn primary" disabled={busy} onClick={onStart}>
                Start now
              </button>
              <button type="button" className="fr-btn" disabled={busy} onClick={onDecline}>
                Take back the invitation
              </button>
            </>
          )}
        </div>
        {t.invited === 'you' && <p className="gh-note">Start now and the computer takes the seat of anyone who has not answered.</p>}
      </div>
    )
  }
  // You left: the computer plays your seat and the others play on.
  if (me !== null && t.seats[me]?.left && t.status === 'active') {
    return (
      <div className="mj-bar">
        <p className="mj-say" role="status">
          You left this table — the computer plays your seat.
        </p>
        <div className="mj-acts">
          <button type="button" className="sheet-btn primary" onClick={onClose}>
            Back to games
          </button>
        </div>
      </div>
    )
  }
  if (t.status === 'over') {
    return (
      <div className="mj-bar">
        <Standings t={t} />
        <div className="mj-acts">
          <button type="button" className="sheet-btn primary" onClick={onClose}>
            Back to games
          </button>
        </div>
      </div>
    )
  }
  if (!p) return null
  if (p.t === 'done' && t.result) {
    return (
      <div className="mj-bar">
        <DealResult t={t} />
        <TimeBar t={t} />
        <div className="mj-acts">
          {p.yours ? (
            <button type="button" className="sheet-btn primary" disabled={busy} onClick={onNext}>
              Next deal
            </button>
          ) : (
            <span className="mj-say">Waiting for the others…</span>
          )}
        </div>
      </div>
    )
  }
  const seq = p.seq
  if (o?.claim && p.t === 'claim' && p.tile !== null) {
    const c = o.claim
    const k = kindOf(p.tile)
    return (
      <div className="mj-bar mine">
        <p className="mj-say" role="status">
          {seatName(t, p.seat ?? 0)} {p.rob ? 'adds to a kong' : 'discarded'}
          <MahjongTile tile={p.tile} className="mj-say-tile" title={kindName(k)} />
          {p.rob ? '— you may rob it' : ''}
        </p>
        <TimeBar t={t} />
        <div className="mj-acts">
          {c.win && (
            <button type="button" className="sheet-btn primary mj-win" disabled={busy} onClick={() => onPlay({ action: 'win', seq })}>
              Win
            </button>
          )}
          {c.kong && (
            <button type="button" className="fr-btn ok" disabled={busy} onClick={() => onPlay({ action: 'kong', seq })}>
              Kong
            </button>
          )}
          {c.pung && (
            <button type="button" className="fr-btn ok" disabled={busy} onClick={() => onPlay({ action: 'pung', seq })}>
              Pong
            </button>
          )}
          {c.chows.map((pair) => (
            <button
              key={pair.join('-')}
              type="button"
              className="fr-btn ok mj-chow"
              disabled={busy}
              aria-label={`Chow with ${pair.map((x) => kindName(x)).join(' and ')}`}
              onClick={() => onPlay({ action: 'chow', with: pair, seq })}
            >
              Chow
              {[...pair, k]
                .sort((a, b) => a - b)
                .map((x) => (
                  <MahjongTile key={x} tile={x * 4} className="mj-btn-tile" />
                ))}
            </button>
          ))}
          <button type="button" className="fr-btn" disabled={busy} onClick={() => onPlay({ action: 'pass', seq })}>
            Pass
          </button>
        </div>
      </div>
    )
  }
  if (t.answered) {
    return (
      <div className="mj-bar">
        <p className="mj-say" role="status">
          You chose {t.answered === 'pung' ? 'pong' : t.answered} — waiting for the others…
        </p>
        <TimeBar t={t} />
      </div>
    )
  }
  if (p.t === 'turn' && p.yours && o) {
    return (
      <div className="mj-bar mine">
        <p className="mj-say" role="status">
          {o.win ? 'Your hand is complete — win, or discard.' : selected !== null ? 'Tap the tile again, or Discard.' : 'Your turn — pick a tile to discard.'}
        </p>
        <TimeBar t={t} />
        <div className="mj-acts">
          {o.win && (
            <button type="button" className="sheet-btn primary mj-win" disabled={busy} onClick={() => onPlay({ action: 'win', seq })}>
              Win
            </button>
          )}
          {o.kongs.map((k) => (
            <button key={k} type="button" className="fr-btn ok" disabled={busy} onClick={() => onPlay({ action: 'kong', kind: k, seq })}>
              Kong
              <MahjongTile tile={k * 4} className="mj-btn-tile" />
            </button>
          ))}
          <button type="button" className="sheet-btn primary" disabled={busy || selected === null} onClick={onDiscard}>
            Discard
          </button>
        </div>
      </div>
    )
  }
  // Someone else's decision.
  const who = p.seat ?? 0
  const seat = t.seats[who]
  const stuck = seat?.kind === 'agent' && !!seat.agent_problem
  return (
    <div className="mj-bar">
      <p className="mj-say" role="status">
        {p.t === 'claim'
          ? 'Waiting for the others…'
          : seat?.kind === 'agent'
            ? stuck
              ? `${seat.name} ${seat.agent_problem === 'offline' ? 'is offline' : seat.agent_problem === 'illegal' ? 'did not answer with a play' : 'could not be reached'} — the computer plays when the time is up`
              : `${seat.name} is thinking…`
            : `${seatName(t, who)}’s turn`}
      </p>
      <TimeBar t={t} />
      {stuck && (
        <div className="mj-acts">
          <button type="button" className="fr-btn" disabled={busy} onClick={onNudge}>
            <Icon name="refresh" size={15} />
            Ask again
          </button>
        </div>
      )}
    </div>
  )
}

/** The deal just over: who won, on what, what it was worth, and what each seat paid or took. */
function DealResult({ t }: { t: MahjongView }) {
  const r = t.result!
  const w = r.win
  return (
    <div className="mj-result" role="status">
      {w ? (
        <>
          <p className="mj-result-head">
            <strong>{seatName(t, w.seat)}</strong> {w.seat === t.you ? 'win' : 'wins'}
            {w.from === null ? ' — self-drawn' : ` on ${whose(t, w.from)} ${w.tile === null ? 'flower' : 'discard'}`}
            {w.tile !== null && <MahjongTile tile={w.tile} className="mj-say-tile" title={kindName(kindOf(w.tile))} />}
          </p>
          <ul className="mj-fans">
            {w.items.map((f) => (
              <li key={f.zh}>
                <span className="mj-fan-zh">{f.zh}</span>
                <span className="mj-fan-en">{f.name}</span>
                <span className="mj-fan-tai">{f.tai} 台</span>
              </li>
            ))}
            {w.dealer_tai > 0 && (
              <li>
                <span className="mj-fan-zh">{w.dealer_tai > 1 ? '莊家 · 連莊' : '莊家'}</span>
                <span className="mj-fan-en">Dealer — paid between the dealer and each payer</span>
                <span className="mj-fan-tai">{w.dealer_tai} 台</span>
              </li>
            )}
            {!w.items.length && (
              <li>
                <span className="mj-fan-zh">屁胡</span>
                <span className="mj-fan-en">No 台 — the base only</span>
                <span className="mj-fan-tai">0 台</span>
              </li>
            )}
          </ul>
        </>
      ) : (
        <p className="mj-result-head">A drawn deal — nobody won (流局). The dealer deals again.</p>
      )}
      <ul className="mj-pay">
        {r.deltas.map((d, s) => (
          <li key={s} className={d > 0 ? 'up' : d < 0 ? 'down' : ''}>
            <span>{seatName(t, s)}</span>
            <span>{signed(d)}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** The end of the game: every seat by its chips, and what it won or lost. */
function Standings({ t }: { t: MahjongView }) {
  const order = t.seats.map((s, i) => ({ s, i })).sort((a, b) => b.s.chips - a.s.chips)
  const why =
    t.reason === 'bust'
      ? 'A seat ran out of chips.'
      : t.reason === 'abandoned'
        ? 'Everyone left the table.'
        : t.reason === 'cancelled'
          ? 'The invitation was taken back.'
          : t.reason === 'declined'
            ? 'The invitation was declined.'
            : `The ${t.settings.rounds === 4 ? 'four rounds are' : 'East round is'} over.`
  return (
    <div className="mj-result" role="status">
      <p className="mj-result-head">Game over. {why}</p>
      <ol className="mj-pay mj-standings">
        {order.map(({ s, i }) => (
          <li key={i} className={s.chips > t.settings.chips ? 'up' : s.chips < t.settings.chips ? 'down' : ''}>
            <span>{seatName(t, i)}</span>
            <span>
              {chips(s.chips)} <span className="mj-delta">({signed(s.chips - t.settings.chips)})</span>
            </span>
          </li>
        ))}
      </ol>
    </div>
  )
}

/** The chips at this table and the stakes: the inviter's choice (邀請者可以預設籌碼). */
function Scores({ t }: { t: MahjongView }) {
  const st = t.settings
  return (
    <section className="mj-scores" aria-label="Chips">
      <p className="gh-note">
        {chips(st.chips)} chips each · base {st.base} + {st.per_tai} a 台 · {st.rounds === 4 ? 'four rounds' : 'the East round'}
      </p>
      {t.history.length > 0 && (
        <ol className="mj-history">
          {t.history
            .slice(-8)
            .reverse()
            .map((h) => (
              <li key={h.hand}>
                <span className="mj-history-n">{h.hand}</span>
                <span>{h.winner === null ? 'Drawn' : `${seatName(t, h.winner)} ${h.from === null ? 'self-drawn' : 'won'} · ${h.tai} 台`}</span>
                <span className={h.deltas[t.you ?? 0]! > 0 ? 'up' : h.deltas[t.you ?? 0]! < 0 ? 'down' : ''}>{signed(h.deltas[t.you ?? 0] ?? 0)}</span>
              </li>
            ))}
        </ol>
      )}
    </section>
  )
}

/** Before the FIRST hint: what it costs, said plainly, and which of your agents to ask. */
function AssistSheet({
  on,
  picked,
  onCancel,
  onAsk,
}: {
  on: boolean
  picked: GameAgentPick | null
  onCancel: () => void
  onAsk: (a: GameAgentPick) => void
}) {
  const [choice, setChoice] = useState<GameAgentPick | null>(picked)
  return (
    <Sheet
      title="AI assist"
      onClose={onCancel}
      className="gm-assist-sheet"
      actions={
        <>
          <button type="button" className="sheet-btn" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="sheet-btn primary" disabled={!choice} onClick={() => choice && onAsk(choice)}>
            {on ? 'Ask for a hint' : 'Turn on and ask'}
          </button>
        </>
      }
    >
      <p className="gh-note gm-assist-say">
        {on
          ? 'AI assist is on for this game. Pick the agent to ask.'
          : 'One of your agents suggests your play. Everyone at the table will see “AI” beside your name for the rest of this game.'}
      </p>
      <AgentPick picked={choice} onPick={setChoice} />
    </Sheet>
  )
}
